import type { AiCapabilities, AiJobStatus, AiProfile, AiReasoningEffort } from '../contracts/ai';
import type { StatusTone } from '../contracts/common';
import type {
  AiProviderState,
  ChatState,
  ContextLayer,
  ContextSourceType,
  CopilotAction,
  CopilotToolName,
} from '../contracts/copilot';

export const COPILOT_ACTION_LABELS: Record<CopilotAction, string> = {
  client_summary: 'Podsumowanie klienta',
  extract_tasks: 'Zadania z tekstu',
  draft_email: 'Szkic odpowiedzi',
  parse_time: 'Czas z tekstu',
  memory_proposal: 'Propozycja pamięci',
  classify_message: 'Klasyfikacja maila',
  day_summary: 'Podsumowanie dnia',
  chat: 'Zapytaj AI',
};

/** Rodzaj zadania AI w historii (akcje Copilota + inne moduły, np. przetworzenie spotkania). */
export function aiJobKindLabel(kind: string): string {
  if (kind in COPILOT_ACTION_LABELS) return COPILOT_ACTION_LABELS[kind as CopilotAction];
  if (kind === 'meeting_process') return 'Notatka ze spotkania';
  if (kind === 'summarize') return 'Podsumowanie';
  if (kind === 'transcribe') return 'Transkrypcja mowy';
  return kind;
}

/** Warstwy kontekstu (MVP §11), np. „Źródła: profil właściciela, klient REA, wątek…”. */
export const CONTEXT_LAYER_LABELS: Record<ContextLayer, string> = {
  system: 'Instrukcja systemu',
  owner: 'Profil właściciela',
  business: 'Profil firmy',
  client: 'Klient',
  project: 'Projekt',
  current: 'Bieżące zadanie',
};

/** Rodzaj źródła w liście „Na podstawie: …”. */
export const CONTEXT_SOURCE_TYPE_LABELS: Record<ContextSourceType, string> = {
  context_profile: 'profil',
  client: 'klient',
  contact: 'kontakt',
  project: 'projekt',
  service: 'usługa',
  task: 'zadanie',
  knowledge_item: 'wiedza',
  meeting: 'spotkanie',
  offer: 'oferta',
  message: 'wiadomość',
  message_thread: 'wątek',
  time_entry: 'czas',
  hosted_asset: 'zasób',
  event: 'zdarzenie',
  approval: 'akceptacja',
  text: 'tekst',
  client_note: 'notatka klienta',
};

/** Profile modeli (fast / smart / exec) — w UI ustawień. */
export const AI_PROFILE_LABELS: Record<AiProfile, string> = {
  fast: 'Szybkie',
  smart: 'Dokładne',
  exec: 'Wykonawcze',
};

export const AI_PROFILE_DESCRIPTIONS: Record<AiProfile, string> = {
  fast: 'Klasyfikacja maili, czas z tekstu, podsumowanie dnia',
  smart: 'Podsumowania klientów, szkice odpowiedzi, zadania, pamięć, „Zapytaj AI”',
  exec: 'Większe zadania agentów (Worker)',
};

/** Przykłady w polu modelu (placeholder) — NIE wartości domyślne; identyfikator wpisuje osoba. */
export const AI_MODEL_PLACEHOLDERS: Record<string, string> = {
  anthropic: 'np. identyfikator modelu z konsoli Anthropic',
  openai: 'np. identyfikator modelu z konsoli OpenAI',
  fake: 'symulacja — model nie jest potrzebny',
};

export const AI_PROVIDER_LABELS: Record<string, string> = {
  fake: 'Symulacja (bez dostawcy)',
  anthropic: 'Anthropic (Claude)',
  openai: 'OpenAI',
};

export function aiProviderLabel(id: string): string {
  return AI_PROVIDER_LABELS[id] ?? id;
}

export const AI_PROVIDER_STATE_LABELS: Record<AiProviderState, string> = {
  simulated: 'Symulacja',
  connected: 'Działa',
  not_connected: 'Niepodłączony',
  auth_lost: 'Utracono autoryzację',
  error: 'Błąd',
};

export const AI_PROVIDER_STATE_TONES: Record<AiProviderState, StatusTone> = {
  simulated: 'accent',
  connected: 'success',
  not_connected: 'neutral',
  auth_lost: 'warning',
  error: 'danger',
};

export const AI_CAPABILITY_LABELS: Record<keyof AiCapabilities, string> = {
  tools: 'narzędzia',
  structuredOutput: 'odpowiedź w formacie JSON',
  vision: 'obraz',
  streaming: 'strumieniowanie',
  reasoningEffort: 'poziom rozumowania',
};

/** Poziom rozumowania profilu (Ustawienia → AI i pamięć → Modele; Fala 9). */
export const AI_REASONING_EFFORT_LABELS: Record<AiReasoningEffort, string> = {
  none: 'Bez rozumowania',
  low: 'Niski',
  medium: 'Średni',
  high: 'Wysoki',
};

export const AI_JOB_STATUS_LABELS: Record<AiJobStatus, string> = {
  queued: 'W kolejce',
  running: 'AI pracuje',
  succeeded: 'Gotowe',
  failed: 'Błąd',
  cancelled: 'Anulowane',
  budget_exceeded: 'Limit kosztów',
};

export const AI_JOB_STATUS_TONES: Record<AiJobStatus, StatusTone> = {
  queued: 'neutral',
  running: 'accent',
  succeeded: 'success',
  failed: 'danger',
  cancelled: 'neutral',
  budget_exceeded: 'warning',
};

export const COPILOT_TOOL_LABELS: Record<CopilotToolName, string> = {
  search: 'Wyszukiwanie',
  get_client_context: 'Kontekst klienta',
  list_tasks: 'Lista zadań',
  create_task: 'Nowe zadanie',
  log_time: 'Wpis czasu',
  draft_email: 'Szkic maila',
  propose_send_email: 'Wysyłka maila',
  propose_memory_update: 'Zmiana pamięci',
  delegate_to_claude: 'Zlecenie dla Claude',
  create_client: 'Nowy klient',
  add_contact: 'Nowy kontakt',
  update_task: 'Zmiana zadania',
  add_note: 'Notatka w Wiedzy',
  create_meeting: 'Nowe spotkanie',
  add_monitor: 'Monitoring strony',
  list_incidents: 'Awarie',
  explain_incident: 'Co się dzieje',
  run_plan: 'Plan działań',
  add_client_note: 'Notatka klienta',
  add_quick_note: 'Szybka notatka',
};

/** Stan AI w panelu „Zapytaj AI” (DESIGN §2.5: AI pracuje, Czeka na akceptację, Gotowe). */
export const CHAT_STATE_LABELS: Record<ChatState | 'working', string> = {
  working: 'AI pracuje',
  done: 'Gotowe',
  needs_confirmation: 'Czeka na Twoje potwierdzenie',
  waiting_approval: 'Czeka na akceptację',
  needs_choice: 'Wybierz, o co chodzi',
  refused: 'Odmowa',
};

export const CHAT_STATE_TONES: Record<ChatState | 'working', StatusTone> = {
  working: 'accent',
  done: 'success',
  needs_confirmation: 'warning',
  waiting_approval: 'accent',
  needs_choice: 'warning',
  refused: 'neutral',
};
